import type { KeyboardEvent } from 'react';

import type { IceFunction, IconPosition, RadioButtonTheme, StringOrNode } from '@snowit/icetypes';
import type { A11yEvent } from '@snowit/icetypes/events';

import useA11y from '@snowit/hooks/useA11y';
import { Keyboard } from '@snowit/iceconst';
import testId from '@snowit/utils/testId';

import Markdown from '../../Typography/Markdown';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './RadioButton.module.scss';

export interface IRadioButton {
  theme?: RadioButtonTheme;
  dotPosition?: IconPosition;
  name?: string;
  label?: string;
  sublabel?: string;
  content?: StringOrNode;
  value: string | number;
  checked?: boolean;
  disabled?: boolean;
  required?: boolean;
  onChange: IceFunction;
  id?: string;
  classNames?: {
    wrapper?: string;
    input?: string;
    wrapLabel?: string;
    label?: string;
    sublabel?: string;
    dot?: string;
  };
  test?: string;
}

const RadioButton = (props: IRadioButton) => {
  const {
    theme = 'default',
    dotPosition = 'after',
    name,
    label,
    sublabel,
    content,
    checked,
    classNames,
    test,
    value,
    onChange,
    disabled,
  } = props;
  const a11yH = useA11y();

  const id = a11yH.makeId(props.id);

  const handleChange = (event: A11yEvent<HTMLInputElement | HTMLDivElement>) => {
    event.stopPropagation();
    if (disabled) return;
    onChange({
      name,
      value,
      id,
      event,
    });
  };

  const handleKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== Keyboard.ENTER) return;
    handleChange(event);
  };

  const renderLabel = () => {
    if (!label) return null;
    return (
      <Typo
        className={cx(s.label, classNames?.label)}
        text={label}
        tag={'label'}
        size={'df'}
        color={'text'}
        a11y={{ htmlFor: id }}
      />
    );
  };

  const renderSublabel = () => {
    if (!sublabel) return null;
    return (
      <Markdown
        classNames={{ text: classNames?.sublabel }}
        source={sublabel}
        baseSize={'sm'}
        color={'text-l'}
      />
    );
  };

  const renderContent = () => {
    if (!label && !content) return null;
    if (!content) {
      return (
        <div className={cx(s.wrapLabel, classNames?.wrapLabel)}>
          {renderLabel()}
          {renderSublabel()}
        </div>
      );
    }
    if (typeof content === 'function') return content();
    return <div className={classNames?.wrapLabel}>{content}</div>;
  };

  const renderDot = () => {
    return (
      <input
        className={cx(s.dot, classNames?.dot)}
        type='radio'
        id={id}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={(event) => handleChange(event)}
        onKeyDown={handleKeydown}
        aria-label={label}
        {...testId(id, test)}
      />
    );
  };

  return (
    <div className={cx(s.wrapper, classNames?.wrapper)} onClick={handleChange}>
      <div
        className={cx(s.input, classNames?.input, {
          [s[theme]]: true,
          [s[dotPosition]]: true,
          [s.checked]: checked,
          [s.disabled]: disabled,
        })}
      >
        {renderContent()}
        {renderDot()}
      </div>
    </div>
  );
};

export default RadioButton;
